import React from "react";
import { Box, Alert, Skeleton, Typography } from "@mui/material";
import { ResponsiveContainer } from "recharts";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BRAND, FONT_BODY } from "../../theme";
import { useSectionAccent } from "../SectionAccentContext";

/* ============================================================
   Tema compartido de gráficos (Recharts)
   Un solo lugar define grilla, ejes, tooltip, leyenda y paleta;
   las páginas solo agregan props propios (dataKey, formatters).
   ============================================================ */

/** Paleta categórica para series múltiples */
export const CHART_SERIES = [
  BRAND.nodoBlue,
  BRAND.fesRed,
  "#0E9594",
  "#E8961E",
  "#6B21A8",
  "#2E7D32",
  "#8D6E63",
  "#607D8B",
];

const TICK = { fontSize: 11, fill: "#6E7688", fontFamily: FONT_BODY };

/** Props base: <CartesianGrid {...ejes.grid} /> · <XAxis {...ejes.x} …/> · <YAxis {...ejes.y} …/> */
export const ejes = {
  grid: { stroke: "#EBEEF5", vertical: false },
  x: { tickLine: false, axisLine: { stroke: "#DFE4EF" }, tick: TICK, tickMargin: 8 },
  y: { tickLine: false, axisLine: false, tick: TICK, tickMargin: 4 },
};

/** Evita que las etiquetas de series mensuales y trimestrales se superpongan. */
export const ejeTemporal = {
  angle: -45,
  textAnchor: "end",
  height: 68,
  tick: { ...TICK, fontSize: 9 },
  tickMargin: 8,
  minTickGap: 24,
  padding: { left: 20, right: 12 },
};

/** Estilo unificado del tooltip: <Tooltip {...tooltipStyle} formatter={…} /> */
export const tooltipStyle = {
  cursor: { stroke: "#AEB8D0", strokeWidth: 1, strokeDasharray: "4 4" },
  wrapperStyle: { outline: "none", zIndex: 5 },
  contentStyle: {
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 10,
    boxShadow: "0 10px 28px -10px rgba(9,20,50,0.24)",
    padding: "9px 12px",
    fontSize: 12,
    fontFamily: FONT_BODY,
  },
  labelStyle: { fontWeight: 700, color: BRAND.text, marginBottom: 4, fontSize: 12 },
  itemStyle: { paddingTop: 1, paddingBottom: 1, fontSize: 12 },
};

/** Cursor para gráficos de barras: <Tooltip {...tooltipStyle} cursor={barCursor} /> */
export const barCursor = { fill: "rgba(11, 61, 145, 0.06)" };

/** Leyenda unificada: <Legend {...legendProps} /> */
export const legendProps = {
  iconType: "circle",
  iconSize: 9,
  wrapperStyle: { fontSize: 12, fontFamily: FONT_BODY, paddingTop: 6 },
};

/* ------------------------------------------------------------
   Estados de carga / error / sin datos
   ------------------------------------------------------------ */

const BARRAS_SKELETON = [42, 66, 34, 78, 55, 88, 48, 70, 62, 84, 38, 74, 58, 80, 46];

/**
 * Skeleton "fantasma" del gráfico: barras que respiran en secuencia (como
 * un ecualizador), un cursor que recorre la serie de izquierda a derecha
 * y un brillo diagonal que barre la tarjeta — todo para comunicar "cargando
 * datos en vivo" en vez de un bloque gris estático.
 */
export function ChartLoading({ height = 300 }) {
  const { accent } = useSectionAccent();

  return (
    <Box
      role="status"
      aria-label="Cargando gráfico"
      sx={{
        height,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        px: 1,
        pb: 1,
        borderRadius: 2,
      }}
    >
      {/* Cursor que recorre el gráfico, como si trazara la serie */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "18%",
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: accent,
          boxShadow: `0 0 0 4px ${accent}22, 0 0 14px 2px ${accent}66`,
          animation: "chart-cursor-travel 2.6s ease-in-out infinite",
        }}
      />

      {/* Barras que "respiran" con retardo escalonado */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 1.25, minHeight: 0, pt: 3 }}>
        {BARRAS_SKELETON.map((h, i) => (
          <Box
            key={i}
            aria-hidden
            sx={{
              flex: 1,
              height: `${h}%`,
              borderRadius: "6px 6px 2px 2px",
              transformOrigin: "bottom",
              background: `linear-gradient(180deg, ${accent}4D, ${accent}1A)`,
              animation: "chart-bar-pulse 1.7s ease-in-out infinite",
              animationDelay: `${i * 0.08}s`,
            }}
          />
        ))}
      </Box>

      <Skeleton variant="rounded" height={8} sx={{ mt: 1.5, borderRadius: 4 }} animation="wave" />

      {/* Brillo diagonal que barre toda la tarjeta */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          width: "40%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
          animation: "chart-shimmer-sweep 2.2s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

export function ChartError({ msg }) {
  return (
    <Alert severity="warning" sx={{ my: 2 }}>
      No se pudo cargar el gráfico: {msg}
    </Alert>
  );
}

/**
 * Reemplaza a <ResponsiveContainer>: si la serie llega vacía, muestra el estado
 * "sin datos" en vez de dibujar unos ejes en blanco. El dato se lee del prop
 * `data` del gráfico hijo, así ningún gráfico necesita su propia comprobación.
 */
export function Grafico({ children, ...props }) {
  if (!children?.props?.data?.length) {
    return (
      <ChartEmpty title="Sin datos disponibles">
        Todavía no hay datos publicados para este indicador. El gráfico aparecerá
        cuando la fuente libere el próximo corte.
      </ChartEmpty>
    );
  }
  return <ResponsiveContainer {...props}>{children}</ResponsiveContainer>;
}

/** Estado "sin datos automatizables": reemplaza los Alert informativos */
export function ChartEmpty({ title = "Datos en preparación", children }) {
  return (
    <Box
      sx={{
        my: 1.5,
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        border: "1.5px dashed #C9D2E4",
        bgcolor: "#F8FAFD",
        display: "flex",
        gap: 1.75,
        alignItems: "flex-start",
      }}
    >
      <InfoOutlinedIcon sx={{ color: BRAND.nodoBlue, fontSize: 20, mt: "2px", flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: BRAND.text, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography component="div" sx={{ fontSize: "0.8rem", color: BRAND.textSub, lineHeight: 1.65 }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
